import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {
    AbstractControl,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn
} from '@angular/forms';
import moment from 'moment';
import {
    combineLatest,
    fromEvent,
    merge,
    Observable,
    ReplaySubject
} from 'rxjs';
import {
    distinctUntilChanged,
    map,
    skip,
    startWith,
    take,
    takeWhile,
    tap,
    throttleTime,
    withLatestFrom
} from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import * as fromLookups from '../../lookups';
import { PayrollItem } from '../../payroll/item/store';
import * as fromShared from '../../shared';
import {
    DisplayMessageMinimum,
    EditGrid,
    IId,
    ResponseStatus
} from '../../shared';
import {
    Accordion,
    DialogService,
    FilterService,
    SelectItem,
    Table
} from '../../shared/efaact-primeng.module';
import { IShowErrors } from '../../shared/errors-sidenav';
import { UnsavedChangesService } from '../../shared/modal-dialog';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromStore from '../store';
import { UserAuthorization } from '../store';
import { UpdateMultipleAuthorizationsComponent } from '../update-multiple-authorizations/update-multiple-authorizations.component';
const ADD_MODE_FILTER_LE =
    'You have a less-than filter in place, this could cause newly added or copied authorizations to not display.';

@Component({
    selector: 'ef-user-authorization',
    templateUrl: './user-authorization.component.html',
    styleUrls: ['./user-authorization.component.scss'],
    standalone: false
})
export class UserAuthorizationComponent
    extends EditGrid<UserAuthorization>
    implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
    @Input() public pendingAuthorizations: fromStore.PendingAuthorization[];
    @Input() public userAuthTypes: SelectItem[];
    @Input() public directReports: fromLookups.IDirectReport[];
    @Input() public activeReports: fromLookups.IDirectReport[];
    @Input() public tasks: SelectItem[];
    @Input() public activeTasks: SelectItem[] = [];
    @Input() public serviceItems: SelectItem[];
    @Input() public activeServiceItems: SelectItem[];
    @Input() public authClasses: SelectItem[];
    @Input() public payrollItems: PayrollItem[];
    @Input() yearOptions: SelectItem[];
    public get uniquePayrollItems(): SelectItem[] {
        return fromShared
            .uniqBy(this.payrollItems, x => x.PayrollItem)
            .map(x => ({ label: x.PayrollItem, value: x.PIListID }));
    }
    @Input() public userGroups: fromLookups.IUserGroup[];
    @Input() public pendingAuthsCount: number;
    @Input() public employeeswithoutauths: boolean;
    @Input() public directReportsFilterSelectItems: SelectItem[];
    @Input() public tasksFilterSelectItems: SelectItem[];
    @Input() public payrollItemsFilterSelectItems: SelectItem[];
    @Input() public serviceItemsFilterSelectItems: SelectItem[];
    @Input() public classItemsFilterSelectItems: SelectItem[];
    @Input() public AuthAutomatedPI: boolean;
    @Input() public useClass: boolean;
    @Input() public modifiedDatesSelectItems: SelectItem[];
    private active = true;
    moment: any;
    public errorHelpText = '';
    public AuthorizationsSubject = new ReplaySubject<UserAuthorization[]>(1);
    public FilteredTasksSubject = new ReplaySubject<fromLookups.ITask[]>(1);
    public FilteredServiceItemsSubject = new ReplaySubject<
        fromLookups.IServiceItem[]
    >(1);
    public operators: SelectItem[] = [
        { value: 'ge', label: 'greater or equal' },
        { value: 'eq', label: 'equal' },
        { value: 'le', label: 'less or equal' }
    ];
    startDateFilter: any;
    endDateFilter: any;
    public filteredItems: UserAuthorization[];
    public displayDetails = true;
    public displayHolidays = true;
    public virtualScroll = true;
    public dropdownItemHeight = environment.dropdownItemHeight;
    public clearingFilters = false;
    public filterMessages: string[] = [];
    public processMessages: string[] = [];
    public filterFormGroup: UntypedFormGroup;
    public addModeFormGroup: UntypedFormGroup;
    public updateFormGroup: UntypedFormGroup;
    public editFormGroup: UntypedFormGroup;
    public editing: UserAuthorization = null as any;
    public payrollItemsForAuth: SelectItem[];
    cloned: { [i: number]: UserAuthorization } = {};
    selected: UserAuthorization[] = [];
    @Output() public ShowError = new EventEmitter<IShowErrors>();
    @Output() public Save = new ReplaySubject<UserAuthorization[]>(1);
    @Output() public Delete = new EventEmitter<IId[]>();
    @Output() public Copy = new EventEmitter<Array<UserAuthorization>>();
    @Output() public AddModeChanged = new EventEmitter<{
        AddAuthMode: boolean;
        endDate?: Date;
        modifiedDate?: Date;
        endDateOperator?: string;
        directReports?: Array<fromLookups.IDirectReport>;
        tasks?: Array<fromLookups.ITask>;
        serviceItems?: Array<Partial<fromLookups.IServiceItem>>;
        classes?: Array<fromLookups.IAuthorizationClass>;
        authTypes?: Array<fromLookups.IAuthorizationType>;
        showSI?: boolean;
        showPI?: boolean;
        EmployeeAuthStartDate?: Date;
        EmployeeAuthEndDate?: Date;
    }>();
    @Output() public AddPending = new EventEmitter<{
        directReports: Array<fromLookups.IDirectReport>;
        tasks: Array<fromLookups.ITask>;
        serviceItems?: Array<Partial<fromLookups.IServiceItem>>;
        classes: Array<fromLookups.IAuthorizationClass>;
        authTypes: Array<fromLookups.IAuthorizationType>;
        endDate: Date;
        AddAuthMode: boolean;
        AddAuthValue: boolean;
        showSI: boolean;
        showPI: boolean;
        EmployeeAuthStartDate: Date;
        EmployeeAuthEndDate: Date;
    }>();
    @Output() public DateFilterChanged = new EventEmitter<{
        EmployeeAuthEndDate: Date;
        AddAuthMode: boolean;
    }>();
    @Output() public SaveError = new EventEmitter<UserAuthorization>();
    @ViewChild('authAccordions', { read: Accordion, static: true })
    authAccordions: Accordion;
    @ViewChild('bulkDelete', { read: ElementRef, static: true })
    bulkDeleteButton: ElementRef;
    @ViewChild('selectAll', { read: ElementRef, static: true })
    selectAllButton: ElementRef;
    @ViewChild('copyAuths', { read: ElementRef, static: true })
    copyAuthsButton: ElementRef;
    @ViewChild('addPendingAuths', { read: ElementRef, static: true })
    addPendingAuthsButton: ElementRef;

    @ViewChild('clearFilters', { read: ElementRef, static: true })
    clearFiltersButton: ElementRef;
    @ViewChild('clearSelectionBtn', { read: ElementRef, static: true })
    clearSelectionButton: ElementRef;
    @ViewChild('clearBulkUpdates', { read: ElementRef, static: true })
    clearBulkUpdatesButton: ElementRef;
    @ViewChild('showDetails', { read: ElementRef, static: true })
    showDetailsButton: ElementRef;
    @ViewChild('refreshAuths', { read: ElementRef, static: true })
    refreshAuthsButton: ElementRef;
    @ViewChild('updateMultipleAuths', { read: ElementRef, static: true })
    updateMultipleButton: ElementRef;
    @ViewChild('dataTable', { read: Table, static: true })
    dtAuthorizations: Table;
    showPastAuths: boolean;
    downloadOptions: { label: string; icon: string; command: () => any }[];
    cols = [
        { field: 'EmployeeName', header: 'User' },
        { field: 'TaskLongName', header: 'Task' },
        { field: 'AuthTypeId', header: 'Type' },
        { field: 'EmpAuthStartDate', header: 'Start Date' },
        { field: 'EmpAuthEndDate', header: 'End Date' },
        { field: 'PayrollItem', header: 'Payroll Item' },
        { field: 'ServiceItem', header: 'Service Item' },
        { field: 'ShowSI', header: 'Show SI on TimeCard' },
        { field: 'ShowPI', header: 'Show PI on TimeCard' },
        { field: 'ModifiedDateTime', header: 'Modified Date' },
        { field: 'MaxHours', header: 'Planned Hours' },
        { field: 'HoursWorked', header: 'Hours Worked' },
        { field: 'ClassListID', header: 'Class' },
        { field: 'PremiumPayIndicator', header: 'Premium' },
        { field: 'AuthDesc', header: 'Work Auth' }
    ];
    activeState: boolean[] = [false, false];
    private updateDialogRef: fromShared.DynamicDialogRef;
    constructor(
        private log: LoggingService,
        protected cdRef: ChangeDetectorRef,
        private confirmservice: UnsavedChangesService,
        private dialogService: DialogService,
        private filterService: FilterService
    ) {
        super();
        this.moment = moment;
        this.filterFormGroup = new UntypedFormGroup({
            endDate: new UntypedFormControl(null)
        });
        this.addModeFormGroup = new UntypedFormGroup({
            task: new UntypedFormControl(null),
            serviceItem: new UntypedFormControl(null),
            authType: new UntypedFormControl(null),
            userGroup: new UntypedFormControl(null),
            directReports: new UntypedFormControl(null),
            authClass: new UntypedFormControl(null),
            addMode: new UntypedFormControl(false),
            showAllUsers: new UntypedFormControl(null),
            showAllTasks: new UntypedFormControl(null),
            showAllServiceItems: new UntypedFormControl(null),
            ShowSI: new UntypedFormControl(false),
            ShowPI: new UntypedFormControl(false),
            EmployeeAuthStartDate: new UntypedFormControl(null),
            EmployeeAuthEndDate: new UntypedFormControl(null)
        });
        this.updateFormGroup = new UntypedFormGroup({
            startDate: new UntypedFormControl(null),
            endDate: new UntypedFormControl(null),
            authType: new UntypedFormControl(null),
            payrollItem: new UntypedFormControl(null),
            serviceItem: new UntypedFormControl(null),
            maxHours: new UntypedFormControl(null),
            authClass: new UntypedFormControl(null),
            ShowSI: new UntypedFormControl(null),
            ShowPI: new UntypedFormControl(null)
        });
        this.editFormGroup = new UntypedFormGroup(
            {
                AuthTypeId: new UntypedFormControl(null),
                EmpAuthStartDate: new UntypedFormControl(null),
                EmpAuthEndDate: new UntypedFormControl(null),
                PIListID: new UntypedFormControl(null),
                SIListID: new UntypedFormControl(null),
                ShowSI: new UntypedFormControl(null),
                ShowPI: new UntypedFormControl(null),
                MaxHours: new UntypedFormControl(null),
                PremiumPayIndicator: new UntypedFormControl(null),
                AuthDesc: new UntypedFormControl(null)
            },
            this.validateEdit
        );
    }
    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        this.log.verbose({
            type: 'UserAuthorizationComponent:SimpleChanges',
            changes
        });

        if (changes.items && changes.items.currentValue) {
            this.filteredItems = changes.items.currentValue;
            this.displayHolidays = true;
        }
    }

    highlightRow(rowId: string, ...styles: string[]) {
        const row = document.getElementById(rowId);
        this.log.verbose({
            type: 'UserAuthorizationComponent:highlightRow',
            rowId,
            row,
            classes: styles
        });
        if (!row) {
            return;
        }
        row.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
        });
        row.classList.add(...styles);
    }
    ngOnInit(): void {
        this.addDateRangeFilters();
        this.setupEventHandlers();
        this.setupSubscriptions();
        // this.dtAuthorizations.sortMode = 'single';
        this.downloadOptions = [
            {
                label: 'CSV',
                icon: 'pi pi-download',
                command: () =>
                    this.dtAuthorizations.exportCSV.bind(
                        this.dtAuthorizations
                    )()
            }
            // {
            //     label: 'Excel',
            //     icon: 'pi pi-download',
            //     command: () => this.dtAuthorizations.exportExcel.bind(this.dtAuthorizations)()
            // }
        ];
        this.handleOnOpen([]);
    }
    ngAfterViewInit() {
        // Trigger change detection to ensure form control bindings are properly evaluated
        this.cdRef.detectChanges();
    }
    ngOnDestroy() {
        this.active = false;
    }
    applyBulkUpdate(values) {
        const controls = this.updateFormGroup.controls;
        const auths: UserAuthorization[] =
            (this.dtAuthorizations && this.dtAuthorizations.selection) || [];
        const mapped = auths.map(x => ({
            ...x,
            EmpAuthStartDate: moment(values.startDate).isValid()
                ? moment(values.startDate).toDate()
                : x.EmpAuthStartDate,
            EmpAuthEndDate: moment(values.endDate).isValid()
                ? moment(values.endDate).toDate()
                : x.EmpAuthEndDate,
            SIListID: values.serviceItem || x.SIListID,
            AuthTypeId: values.authType || x.AuthTypeId,
            MaxHours: values.maxHours || x.MaxHours,
            PIListID: values.payrollItem || x.PIListID,
            ClassListID: values.authClass || x.ClassListID,
            ShowSI: values.ShowSI !== null ? values.ShowSI : x.ShowSI,
            ShowPI: values.ShowPI !== null ? values.ShowPI : x.ShowPI,
            PremiumPayIndicator: values.PremiumPayIndicator !== null ? values.PremiumPayIndicator : x.PremiumPayIndicator,
        }));
        this.log.verbose({
            type: 'UserAuthorizationsComponent:applyBulkButton:ApplyClick',
            controls,
            auths,
            mapped
        });
        this.Save.next(mapped);
        this.selected = [];
    }
    setupEventHandlers() {
        fromEvent(this.clearSelectionButton.nativeElement, 'click')
            .pipe(
                takeWhile(() => this.active),
                tap(x =>
                    this.log.verbose({
                        type: 'UserAuthorizationComponent:clearSelection:click'
                    })
                )
            )
            .subscribe(() => {
                this.selected = [];
            });

        fromEvent(this.clearFiltersButton.nativeElement, 'click')
            .pipe(
                takeWhile(x => this.active),
                throttleTime(500),
                tap(x =>
                    this.log.verbose({
                        type: 'UserAuthorizationComponent:clearFilters:click'
                    })
                )
            )
            .subscribe(x => {
                this.processMessages = [];
                this.clearFilters(
                    this.addModeFormGroup.controls['addMode'].value
                );
            });

        fromEvent(this.bulkDeleteButton.nativeElement, 'click')
            .pipe(
                takeWhile(x => this.active),
                throttleTime(500)
            )
            .subscribe(x => {
                this.processMessages = [];
                this.onDelete(this.selected);
            });
        fromEvent(this.refreshAuthsButton.nativeElement, 'click')
            .pipe(takeWhile(() => this.active))
            .subscribe(() => {
                const addModeToggle = this.addModeFormGroup.controls['addMode'];
                if (addModeToggle.value) {
                    addModeToggle.setValue(false);
                    this.activeState[1] = false;
                    // this.cdRef.detectChanges();
                }
            });
        merge(
            this.filterFormGroup.valueChanges,
            this.updateFormGroup.valueChanges
        )
            .pipe(takeWhile(() => this.active))
            .subscribe(() => {
                this.processMessages = [];
            });

        merge(
            fromEvent(this.addPendingAuthsButton.nativeElement, 'click'),
            fromEvent(this.copyAuthsButton.nativeElement, 'click')
        )
            .pipe(takeWhile(() => this.active))
            .subscribe(() => {
                this.processMessages = [
                    'After adding new items you can easily find them by sorting on Modified Date'
                ];
            });
        fromEvent(this.updateMultipleButton.nativeElement, 'click')
            .pipe(takeWhile(() => this.active))
            .subscribe(() => {
                this.updateDialogRef = this.dialogService.open(
                    UpdateMultipleAuthorizationsComponent,
                    {
                        header: 'Update Multiple Authorizations',
                        closable: false,
                        closeOnEscape: false,
                        showHeader: false,
                        modal: true,
                        data: {
                            userAuthTypes: this.userAuthTypes,
                            uniquePayrollItems: this.uniquePayrollItems,
                            serviceItems: this.serviceItems,
                            authClasses: this.authClasses
                        }
                    }
                );
                this.updateDialogRef.onClose.pipe(take(1)).subscribe(value => {
                    this.log.verbose({
                        type: 'UserAuthorizationComponent:afterClosed',
                        value
                    });

                    if (value) {
                        if (
                            this.selected.length ===
                            this.dtAuthorizations.value.length
                        ) {
                            this.confirmservice
                                .confirmPromise({
                                    okClass: 'p-button-danger p-button-text',
                                    dialogText: `You have all ${this.selected.length} items selected, are you sure?`
                                })
                                .then(() => {
                                    this.applyBulkUpdate(value);
                                });
                        } else {
                            this.applyBulkUpdate(value);
                        }
                    }
                });
            });
    }
    setupSubscriptions() {
        const filterControls = this.filterFormGroup.controls;
        const addControls = this.addModeFormGroup.controls;
        const filterDatesChanged$ = filterControls['endDate'].valueChanges.pipe(
            // combineLatest([
            startWith(null),
            map(x => x ? new Date(x, 0, 1) : null),
            // map(x => x.getUTCFullYear() === new Date().getUTCFullYear() ? new Date() : x),
            tap(endDate =>
                this.log.verbose({
                    type: 'UserAuthorizationComponent:endDate:valuechanges',
                    endDate
                })
            ),
            distinctUntilChanged()
        ) as Observable<Date>;

        // ]);
        // .pipe(share())
        const addMode$ = addControls[
            'addMode'
        ].valueChanges.pipe() as Observable<boolean>;

        addMode$.pipe(takeWhile(() => this.active)).subscribe(addmode => {
            if (!addmode) {
                this.activeState[0] = false;
            }
        });

        const combinedClientFilters$ = combineLatest([
            addControls['authType'].valueChanges.pipe(
                startWith(addControls['authType'].value)
            ),
            addControls['directReports'].valueChanges.pipe(
                startWith(addControls['directReports'].value)
            ),
            addControls['task'].valueChanges.pipe(
                startWith(addControls['task'].value)
            ),
            addControls['serviceItem'].valueChanges.pipe(
                startWith(addControls['serviceItem'].value)
            ),
            addControls['authClass'].valueChanges.pipe(
                startWith(addControls['authClass'].value)
            ),
            addControls['ShowSI'].valueChanges.pipe(
                startWith(addControls['ShowSI'].value)
            ),
            addControls['ShowPI'].valueChanges.pipe(
                startWith(addControls['ShowPI'].value)
            ),
            addControls['EmployeeAuthStartDate'].valueChanges.pipe(
                startWith(addControls['EmployeeAuthStartDate'].value)
            ),
            addControls['EmployeeAuthEndDate'].valueChanges.pipe(
                startWith(addControls['EmployeeAuthEndDate'].value)
            )
        ]).pipe(
            distinctUntilChanged(
                (prev, curr) =>
                    prev[0] === curr[0] &&
                    prev[1] === curr[1] &&
                    prev[2] === curr[2] &&
                    prev[3] === curr[3] &&
                    prev[4] === curr[4] &&
                    prev[5] === curr[5] &&
                    prev[6] === curr[6] &&
                    prev[7] === curr[7] &&
                    prev[8] === curr[8]
            ),
            tap(x =>
                this.log.verbose({
                    type: 'UserAuthorizationComponent:combinedClientFitlers$',
                    x
                })
            )
        );

        // Single subscription to handle both immediate date changes and refresh button clicks
        // Using merge to combine immediate date changes with refresh-triggered date changes
        merge(
            // Immediate date changes
            filterDatesChanged$.pipe(
                withLatestFrom(addMode$.pipe(startWith(false))),
                map(([endDate, addMode]) => ({
                    source: 'immediate',
                    endDate,
                    addMode
                }))
            ),
            // Refresh button clicks
            combineLatest([
                fromEvent(this.refreshAuthsButton.nativeElement, 'click'),
                filterDatesChanged$
            ]).pipe(
                withLatestFrom(addMode$.pipe(startWith(false))),
                map(([[click, endDate], addMode]) => ({
                    source: 'refresh',
                    endDate,
                    addMode
                }))
            )
        )
            .pipe(
                takeWhile(() => this.active),
                tap(x =>
                    this.log.verbose({
                        type: 'UserAuthorizationComponent:merged:filterDatesChanged$',
                        x
                    })
                )
            )
            .subscribe(({ source, endDate, addMode }) => {
                if (source === 'immediate' && endDate) {
                    // For immediate date changes, always emit (but only if endDate is not null)
                    this.DateFilterChanged.emit({
                        EmployeeAuthEndDate: endDate,
                        AddAuthMode: addMode
                    });
                } else if (source === 'refresh' && !addMode && endDate) {
                    // For refresh button, only emit if not in add mode and endDate is not null
                    this.DateFilterChanged.emit({
                        EmployeeAuthEndDate: this.showPastAuths
                            ? endDate
                            : moment().add(-1, 'days').toDate(),
                        AddAuthMode: addMode
                    });
                }
            });

        fromEvent(this.copyAuthsButton.nativeElement, 'click')
            .pipe(
                takeWhile(x => this.active),
                throttleTime(1000),
                withLatestFrom(filterDatesChanged$),
                tap(() =>
                    this.log.verbose({
                        type: 'UserAuthorizationsComponent:copyAuthsButton:ApplyClick'
                    })
                )
            )
            .subscribe(([click, endDate]) => {
                this.selected.forEach(row => {
                    row.Status.StatusCode = ResponseStatus.Loading;
                });
                this.Copy.emit(this.selected);
                this.selected = [];
            });

        fromEvent(this.addPendingAuthsButton.nativeElement, 'click')
            .pipe(
                takeWhile(x => this.active),
                withLatestFrom(filterDatesChanged$),
                withLatestFrom(combinedClientFilters$),
                distinctUntilChanged(),
                tap(x =>
                    this.log.verbose({
                        type: 'UserAuthorizationComponent:addPendingAuthsButton:click',
                        x
                    })
                )
            )
            .subscribe(
                ([
                    [click, endDate],
                    [
                        authTypes,
                        directReports,
                        tasks,
                        serviceItems,
                        classes,
                        showSI,
                        showPI,
                        EmployeeAuthStartDate,
                        EmployeeAuthEndDate
                    ]
                ]) => {
                    this.AddPending.emit({
                        directReports,
                        tasks:
                            tasks &&
                            tasks.map(x => ({
                                TaskListID: x,
                                ContractType: null,
                                LaborType: '',
                                TaskLongName: ''
                            })),
                        serviceItems: serviceItems
                            ? [{ SIListID: serviceItems }]
                            : undefined,
                        classes,
                        authTypes:
                            authTypes &&
                            authTypes.map(x => ({
                                AuthTypeId: x,
                                AuthType: '',
                                AuthTypeDesc: '',
                                MaintDate: null
                            })),
                        endDate,
                        AddAuthMode: true,
                        AddAuthValue: true,
                        showSI: showSI === true,
                        showPI: showPI === true,
                        EmployeeAuthStartDate,
                        EmployeeAuthEndDate
                    });
                    this.clearFilters();
                }
            );
        combineLatest([addMode$, combinedClientFilters$])
            .pipe(
                takeWhile(() => this.active),
                withLatestFrom(
                    this.DateFilterChanged.pipe(
                        map(x => x.EmployeeAuthEndDate),
                        // https://github.com/eFAACT/eFAACTCentral/issues/3421
                        // startWith(moment().add(-1, 'days').toDate())
                        startWith(undefined)
                    )
                ),
                // withLatestFrom(filterDatesChanged$)
                // skip(1),
                distinctUntilChanged()
            )
            .subscribe(
                ([
                    [
                        addMode,
                        [
                            authTypes,
                            directReports,
                            tasks,
                            serviceItems,
                            authClasses,
                            showSI,
                            showPI,
                            EmployeeAuthStartDate,
                            EmployeeAuthEndDate
                        ]
                    ],
                    endDate
                ]) => {
                    if (!addMode) {
                        this.activeState[1] = false;
                    }
                    this.log.verbose({
                        type: 'UserAuthorizationComponent:addMode$',
                        addMode,
                        authClasses,
                        authTypes,
                        clearingFilters: this.clearingFilters,
                        directReports,
                        serviceItems,
                        showSI,
                        showPI,
                        tasks,
                        endDate,
                        EmployeeAuthStartDate,
                        EmployeeAuthEndDate: EmployeeAuthEndDate
                            ? EmployeeAuthEndDate
                            : endDate
                    });
                    if (addMode && !this.clearingFilters) {
                        this.log.verbose({
                            type: 'UserAuthorizationComponent:addMode$:emitting:AddModeChanged'
                        });
                        // this.showPastAuths = false;
                        // this.filterFormGroup.controls['endDate'].patchValue(
                        //     new Date().getUTCFullYear()
                        // );
                        // this.dtAuthorizations.editMode = 'none';
                        // this.dtAuthorizations.selectionMode = 'none';
                        this.AddModeChanged.emit({
                            AddAuthMode: addMode,
                            endDate,
                            directReports: directReports,
                            tasks:
                                tasks &&
                                tasks.map(x => ({
                                    TaskListID: x,
                                    ContractType: null,
                                    LaborType: '',
                                    TaskLongName: ''
                                })),
                            serviceItems: serviceItems && [
                                {
                                    SIListID: serviceItems,
                                    ServiceItem: '',
                                    ServiceItemDesc: ''
                                }
                            ],
                            classes: authClasses,
                            authTypes:
                                authTypes &&
                                authTypes.map(x => ({
                                    AuthTypeId: x,
                                    AuthType: '',
                                    AuthTypeDesc: '',
                                    MaintDate: null
                                })),
                            showSI,
                            showPI,
                            EmployeeAuthStartDate,
                            EmployeeAuthEndDate
                        });
                    } else {
                        if (!this.clearingFilters) {
                            // this.addModeFormGroup.controls['ShowSI'].setValue(null);
                            this.clearFilters(false);
                            // this.dtAuthorizations.editMode = 'row';
                            // this.dtAuthorizations.selectionMode = 'multiple';
                            this.AddModeChanged.emit({
                                AddAuthMode: addMode,
                                endDate,
                                showSI,
                                showPI
                            });
                        }
                    }
                }
            );
    }
    onRowEditInit(item: UserAuthorization) {
        super.onRowEditInit(item);
        this.getPayrollItemsForAuth(item);
        this.log.verbose({
            type: 'UserAuthorizationComponent:onRowEditInit',
            item,
            cloned: this.cloned,
            editing: this.editing
        });
        if (!moment(item.EmpAuthStartDate).isValid()) {
            item.EmpAuthStartDate = undefined;
        }
        if (!moment(item.EmpAuthEndDate).isValid()) {
            item.EmpAuthEndDate = undefined;
        }
    }
    onRowEditSave(item: UserAuthorization) {
        this.log.verbose({
            type: 'UserAuthorizationComponent:onRowEditSave',
            r: item,
            cloned: this.cloned
        });
        //this.editFormGroup.updateValueAndValidity();
        //if (this.editFormGroup.valid) {
            item.Status.StatusCode = ResponseStatus.Loading;
            item.Status.Message = 'Saving';
            item.Status.MsgEntityID = item.id;
            this.Save.next([item]);
            delete this.cloned[item.id];
            delete this.editing;
        // } else {
        //     item.Status.StatusCode = ResponseStatus.ClientError;
        //     item.Status.Message = Object.keys(this.editFormGroup.errors).reduce(
        //         (a, c) => {
        //             return a + `${c} - ${this.editFormGroup.errors[c]}\r\n`;
        //         },
        //         ''
        //     );
        //     this.SaveError.next(item);
        //     this.dataTable.initRowEdit(item);
        // }
    }
    onRowEditCancel(item: UserAuthorization) {
        this.log.verbose({
            type: 'UserAuthorizationComponent:onRowEditCancel',
            r: item,
            cloned: this.cloned
        });

        const index = this.items.findIndex(x => x.id === item.id);
        const revert = this.cloned[item.id];

        if (index >= 0) {
            // this.items = [
            //     ...this.items.slice(0, index),
            //     {
            //         ...revert,
            //         Status:
            //             revert.Status.StatusCode === ResponseStatus.ClientError
            //                 ? {
            //                       StatusCode: ResponseStatus.None,
            //                       Message: null
            //                   }
            //                 : revert.Status
            //     },
            //     ...this.items.slice(index + 1)
            // ];
            this.items.splice(index, 1, this.cloned[item.id]);
            this.items = [...this.items];
        }
        this.selected = [];

        delete this.cloned[item.id];
        delete this.editing;
    }
    onStatusClick(item: UserAuthorization) {
        this.log.verbose({
            type: 'UserAuthorizationComponent:onStatusClick',
            item
        });
        this.ShowError.emit({ open: true, MsgEntityID: item.id });
    }
    getPayrollItemsForAuth(rowData: UserAuthorization) {
        this.log.verbose({
            type: 'UserAuthorizationComponent:payrollItemsForAuth',
            payrollItems: this.payrollItems
        });
        const filtered = this.payrollItems
            .filter(p => p.EmployeeListID === rowData.EmployeeListID)
            .map(x => ({
                label: x.PayrollItem,
                value: x.PIListID
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
        this.log.verbose({
            type: 'UserAuthorizationComponent:payrollItemsForAuth',
            filtered
        });
        this.payrollItemsForAuth = filtered;
        // return filtered;
    }
    onDelete(items: UserAuthorization[]) {
        this.confirmservice
            .confirmPromise({
                okClass: 'p-button-danger p-button-text',
                dialogText: `Are you sure you want to delete ${
                    items.length > 1
                        ? `the ${items.length} selected Authorizations?`
                        : 'this Authorization'
                }?`
            })
            .then(() => {
                this.selected = [];
                this.Delete.emit(items.map(x => x.id));
            });
    }
    clearFilters(emit = false): void {
        this.log.verbose({
            type: 'UserAuthorizationComponent:clearFilters',
            emit
        });
        this.clearingFilters = !emit;
        const controls = this.addModeFormGroup.controls;
        // const myDate = new Date(
        //     Date.UTC(
        //         new Date().getUTCFullYear(),
        //         new Date().getUTCMonth(),
        //         new Date().getUTCDate()
        //     )
        // );
        controls['authType'].setValue(null);
        controls['directReports'].setValue(null);
        controls['task'].setValue(null);
        controls['serviceItem'].setValue(null);
        controls['authClass'].setValue(null);
        controls['showAllUsers'].setValue(false);
        controls['showAllTasks'].setValue(false);
        controls['showAllServiceItems'].setValue(false);
        if (!this.addModeFormGroup.controls['addMode'].value) {
            controls['ShowSI'].setValue(null);
        }
        if (!this.addModeFormGroup.controls['addMode'].value) {
            controls['ShowPI'].setValue(null);
        }
        this.clearingFilters = false;
    }
    addDateRangeFilters() {
        this.filterService.register(
            'startDateRangeFilter',
            (value, filter): boolean => {
                this.log.verbose({
                    type: 'UserAuthorizationComponent:startDateRangeFilter',
                    value,
                    filter
                });
                if (value === undefined || value === null) {
                    return false;
                }
                return moment(value).isBetween(
                    moment(this.startDateFilter[0]),
                    moment(this.startDateFilter[1])
                );
                // get the from/start value
            }
        );
        this.filterService.register(
            'endDateRangeFilter',
            (value, filter): boolean => {
                this.log.verbose({
                    type: 'UserAuthorizationComponent:endDateRangeFilter',
                    value,
                    filter
                });
                if (value === undefined || value === null) {
                    return false;
                }
                return moment(value).isBetween(
                    moment(this.endDateFilter[0]),
                    moment(this.endDateFilter[1])
                );
                // get the from/start value
            }
        );
    }
    filterPayroll($event) {
        this.dtAuthorizations.filter(
            $event.value.map(x => x.PIListID),
            'PIListID',
            'in'
        );
    }
    filterServiceItem($event) {
        this.dtAuthorizations.filter(
            $event.value.map(x => x.SIListID),
            'SIListID',
            'in'
        );
    }
    handleOnOpen($event) {
        const baseHeight = '69vh';
        let accHeight = 0;
        if ($event === 0 || $event.includes(0)) {
            accHeight += 13;
        } else {
            if (this.addModeFormGroup.controls['addMode'].value === true) {
                this.addModeFormGroup.controls['addMode'].setValue(false);
            }
        }

        this.dtAuthorizations.scrollHeight = `calc(${baseHeight} - ${accHeight}em)`;
        this.log.verbose({
            type: 'UserAuthorizationComponent:handleOnOpen',
            $event,
            scrollHeight: this.dtAuthorizations.scrollHeight
        });
        this.cdRef.detectChanges();
    }
    updateShowPastAuths(value: boolean) {
        this.log.verbose({
            type: 'UserAuthorizationComponent:updateShowPastAuths',
            value
        });
        if (value) {
            this.DateFilterChanged.emit({
                EmployeeAuthEndDate: new Date(
                    this.filterFormGroup.controls['endDate'].value,
                    0,
                    1
                ),
                AddAuthMode: this.addModeFormGroup.controls['addMode'].value
            });
        } else {
            this.filterFormGroup.controls['endDate'].setValue(
                new Date().getUTCFullYear()
            );
            this.DateFilterChanged.emit({
                EmployeeAuthEndDate: moment().add(-1, 'days').toDate(),
                AddAuthMode: this.addModeFormGroup.controls['addMode'].value
            });
        }
    }
    onDisplayHolidaysChange(showHolidays) {
        this.filteredItems = this.items.filter(
            x => showHolidays || !x.IsHoliday
        );
        this.log.verbose({
            type: 'UserAuthorizationComponent:onDisplayHolidaysChange',
            showHolidays,
            auths: this.items,
            filtered: this.filteredItems
        });
    }
    hasErrorStatus = auth =>
        auth && auth.Status && auth.Status.StatusCode >= DisplayMessageMinimum;

    isLink = text =>
        /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[\-;:&=\+\$,\w]+@)?[A-Za-z0-9\.\-]+|(?:www\.|[\-;:&=\+\$,\w]+@)[A-Za-z0-9\.\-]+)((?:\/[\+~%\/\.\w\-_]*)?\??(?:[\-\+=&;%@\.\w_]*)#?(?:[\.\!\/\\\w]*))?)/.test(
            text
        );

    validateEdit: ValidatorFn = (
        ctrl: AbstractControl
    ): ValidationErrors | null => {
        this.log.verbose({
            type: 'UserAuthorizationComponent:validateEdit',
            ctrl
        });
        const startDate = ctrl.get('EmpAuthStartDate');
        const endDate = ctrl.get('EmpAuthEndDate');

        if (moment(startDate.value).isAfter(moment(endDate.value))) {
            return {
                ['Authorization Dates']: 'Start date cannot be after End Date'
            };
        }

        return null;
    };
    onRowSelect($event) {
        super.onRowSelect($event);
        this.log.verbose({
            type: 'UserAuthorizationComponent:onRowSelect',
            $event,
            selected: this.selected
        });
        if (this.selected.length > 0) {
            this.activeState[1] = true;
        } else {
            this.activeState[1] = false;
        }
    }

    onYearFilterClear() {
        this.log.verbose({
            type: 'UserAuthorizationComponent:onYearFilterClear'
        });

        // When cleared, go back to default table load (current year, no past authorizations)
        this.DateFilterChanged.emit({
            EmployeeAuthEndDate: moment().add(-1, 'days').toDate(),
            AddAuthMode: this.addModeFormGroup.controls['addMode'].value
        });
    }

    taskWidth = () =>
        this.activeTasks.reduce((a, c) => Math.max(a, c.label.length), 0) / 2 + 'em';

    get isAddMode(): boolean {
        return !!this.addModeFormGroup?.get('addMode')?.value;
    }
}
